import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { api } from '../../lib/api'
import { WebMToWavConverter, toBase64 } from '../../lib/wav'
import { ToggleButton } from '../ToggleButton'

const BAR_COUNT = 60
const BAR_WIDTH = 6
const BAR_GAP = 8
const MAX_HEIGHT_FACTOR = 0.8
const LOW_FREQ_SCALE = 0.2
const HIGH_FREQ_SCALE = 3.5
const SMOOTHING_FACTOR = 0.5
const BASELINE_VOLUME_THRESHOLD = 0.1
const BASELINE_HEIGHT = 0.015
const BAR_COLOR = '#a3ffa3'

export function VoicePromptPanel() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioDataRef = useRef<Uint8Array | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const wavConverterRef = useRef<WebMToWavConverter | null>(null)
  const barHeightsRef = useRef<number[]>(new Array(BAR_COUNT).fill(0))
  const prevBarHeightsRef = useRef<number[]>(new Array(BAR_COUNT).fill(0))
  const containerRef = useRef<HTMLDivElement>(null)
  const spaceActiveRef = useRef(false)
  const stopRequestRef = useRef(false)

  const [isCapturing, setIsCapturing] = useState(false)
  const [isContainerFocused, setIsContainerFocused] = useState(false)
  const [submitEnabled, setSubmitEnabled] = useState(true)

  const handleCanvasPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    containerRef.current?.focus()
    stopRequestRef.current = false
    // const mediaRecorder = mediaRecorderRef.current
    // if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    //   return
    // }
    // if (!isCapturing) {
    //   void handleCapture()
    // }
  }

  const handleCanvasPointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    event.preventDefault()
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    // const mediaRecorder = mediaRecorderRef.current
    // if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    //   void handleStop()
    // } else {
    //   stopRequestRef.current = true
    // }
  }

  const handleCanvasPointerCancel = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      void handleStop()
    } else {
      stopRequestRef.current = true
    }
  }

  const handleContainerFocus = () => {
    setIsContainerFocused(true)
  }

  const handleContainerBlur = () => {
    spaceActiveRef.current = false
    stopRequestRef.current = false
    setIsContainerFocused(false)
    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      void handleStop()
    }
  }

  const handleContainerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault()
      if (event.repeat) {
        return
      }
      spaceActiveRef.current = true
      stopRequestRef.current = false
      const mediaRecorder = mediaRecorderRef.current
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        void handleCapture()
      }
    }
  }

  const handleContainerKeyUp = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault()
      spaceActiveRef.current = false
      const mediaRecorder = mediaRecorderRef.current
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        void handleStop()
      } else {
        stopRequestRef.current = true
      }
    }
  }

  const focusContainer = () => {
    containerRef.current?.focus()
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const parent = canvas.parentElement
    if (!parent) return

    const resize = () => {
      const { clientWidth, clientHeight } = parent
      if (clientWidth && clientHeight) {
        canvas.width = clientWidth
        canvas.height = clientHeight
      }
    }

    resize()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(resize)
    observer.observe(parent)

    return () => observer.disconnect()
  }, [])

  const handleCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Set up MediaRecorder for audio recording
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.start()

      // Set up audio visualization
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyserRef.current = analyser

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      audioDataRef.current = new Uint8Array(analyser.frequencyBinCount)
      barHeightsRef.current = new Array(BAR_COUNT).fill(0)
      prevBarHeightsRef.current = new Array(BAR_COUNT).fill(0)
      setIsCapturing(true)

      if (stopRequestRef.current) {
        stopRequestRef.current = false
        void handleStop()
      }
    } catch (error) {
      console.error('Error accessing microphone:', error)
    }
  }

  const handleStop = async () => {
    // Stop capturing immediately (for UI feedback)
    setIsCapturing(false)
    spaceActiveRef.current = false
    stopRequestRef.current = false

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()

      // Process transcription in background
      const processTranscription = async () => {
        try {
          // Create audio blob from chunks
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })

          // Filter out recordings less than 0.5 seconds based on WebM blob size
          // WebM is compressed, but typical WebM opus audio is ~6-12 KB/sec
          // Using 3KB as minimum (conservative estimate for 0.5 sec)
          const minimumWebMSize = 3000

          if (audioBlob.size < minimumWebMSize) {
            console.log('Audio too short, skipping transcription. WebM size:', audioBlob.size)
            return
          }

          if (!wavConverterRef.current) {
            wavConverterRef.current = new WebMToWavConverter()
          }

          const wavBlob = await wavConverterRef.current.convertWebMToWav(audioBlob)
          const wavArrayBuffer = await wavBlob.arrayBuffer()
          const wavData = new Uint8Array(wavArrayBuffer)
          const base64Audio = toBase64(wavData)

          // Call transcribe API
          console.log('Transcribing audio, size:', wavData.length)
          const aiResponse = await api().transcribe(base64Audio)
          console.log('Transcription result:', aiResponse)

          const transcriptText =
            (typeof aiResponse?.text === 'string' && aiResponse.text) ||
            (typeof aiResponse?.response?.text === 'string' && aiResponse.response.text) ||
            ''

          if (transcriptText.trim() && submitEnabled) {
            await api().agent.prompt(transcriptText.trim())
          }
        } catch (error) {
          console.error('Error transcribing audio:', error)
        }
      }

      // Wait for the final data to be available, then process
      mediaRecorderRef.current.onstop = () => {
        void processTranscription()
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    analyserRef.current = null
    audioDataRef.current = null
    mediaRecorderRef.current = null
    audioChunksRef.current = []
    barHeightsRef.current = new Array(BAR_COUNT).fill(0)
    prevBarHeightsRef.current = new Array(BAR_COUNT).fill(0)
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const animate = () => {
      const width = canvas.width
      const height = canvas.height

      const barCount = barHeightsRef.current.length

      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      const currentHeights = barHeightsRef.current
      const previousHeights = [...prevBarHeightsRef.current]

      if (isCapturing && analyserRef.current && audioDataRef.current) {
        analyserRef.current.getByteFrequencyData(audioDataRef.current)

        const frequencyData = audioDataRef.current
        let totalVolume = 0
        for (let i = 0; i < frequencyData.length; i++) {
          totalVolume += frequencyData[i]
        }
        const avgVolume = totalVolume / (frequencyData.length * 255)
        const binSize = frequencyData.length / barCount

        for (let i = 0; i < barCount; i++) {
          const startIndex = Math.floor(i * binSize)
          const endIndex = Math.max(startIndex + 1, Math.floor((i + 1) * binSize))

          let sum = 0
          let samples = 0
          for (let j = startIndex; j < endIndex && j < frequencyData.length; j++) {
            sum += frequencyData[j]
            samples++
          }

          const average = samples > 0 ? sum / samples : 0
          let normalizedHeight = average / 255
          const position = i / (barCount - 1 || 1)
          const scaleFactor = LOW_FREQ_SCALE + position * (HIGH_FREQ_SCALE - LOW_FREQ_SCALE)
          normalizedHeight = Math.max(0, Math.min(1, normalizedHeight * scaleFactor))

          if (avgVolume < BASELINE_VOLUME_THRESHOLD) {
            normalizedHeight = Math.max(normalizedHeight, BASELINE_HEIGHT)
          }

          currentHeights[i] =
            previousHeights[i] * SMOOTHING_FACTOR + normalizedHeight * (1 - SMOOTHING_FACTOR)
        }
      } else {
        const baselineTarget = BASELINE_HEIGHT
        for (let i = 0; i < barCount; i++) {
          currentHeights[i] =
            previousHeights[i] * SMOOTHING_FACTOR + baselineTarget * (1 - SMOOTHING_FACTOR)
        }
      }

      for (let i = 0; i < barCount; i++) {
        prevBarHeightsRef.current[i] = currentHeights[i]
      }

      const totalBarWidth = (BAR_WIDTH + BAR_GAP) * barCount - BAR_GAP
      const startX = (width - totalBarWidth) / 2
      const centerY = height / 2
      const maxBarHeight = height * MAX_HEIGHT_FACTOR

      ctx.fillStyle = BAR_COLOR
      for (let i = 0; i < barCount; i++) {
        const amplitude = currentHeights[i]
        if (amplitude <= 0.01) continue

        const barHeight = amplitude * maxBarHeight
        if (barHeight <= 0) continue

        const x = startX + i * (BAR_WIDTH + BAR_GAP)
        const y = centerY - barHeight / 2

        ctx.fillRect(x, y, BAR_WIDTH, barHeight)
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isCapturing])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleStop()
    }
  }, [])

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <button
          className="btn"
          onClick={handleCapture}
          disabled={isCapturing}
          title="Start capturing audio from microphone"
        >
          Capture
        </button>
        <button
          className="btn"
          onClick={handleStop}
          disabled={!isCapturing}
          title="Stop capturing audio"
        >
          Stop
        </button>
        <ToggleButton
          label="Submit"
          enabled={submitEnabled}
          onToggle={setSubmitEnabled}
          disabled={isCapturing}
        />
      </div>
      <div
        ref={containerRef}
        className={
          isContainerFocused
            ? 'voice-prompt-panel-content voice-prompt-panel-focused'
            : 'voice-prompt-panel-content'
        }
        tabIndex={0}
        role="button"
        aria-label="Voice prompt capture"
        aria-pressed={isCapturing}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        onKeyDown={handleContainerKeyDown}
        onKeyUp={handleContainerKeyUp}
        onPointerDown={focusContainer}
        onClick={focusContainer}
      >
        <canvas
          ref={canvasRef}
          width={800}
          height={400}
          className="voice-prompt-canvas"
          onPointerDown={handleCanvasPointerDown}
          onPointerUp={handleCanvasPointerUp}
          onPointerCancel={handleCanvasPointerCancel}
        />
      </div>
    </div>
  )
}
